/**
 * Tenant user login mutation (Batches 05 + 31a.2.1).
 *
 * The backend ``/auth/login`` app-host route returns ONE OF two shapes
 * per ``SAAS_ARCHITECTURE.md`` MFA section, gated on
 * ``tenants.mfa_required``:
 *
 *   - mfa_required=false (default): ``{access_token, ...}`` direct mint.
 *   - mfa_required=true: ``{mfa_pending_token, next_step, ...}``.
 *
 * Per HARD-9 the hook clears any prior mfa_pending state on every
 * mutate (onMutate) and on error (onError). Per HARD-10 the
 * mfa_pending token lives in-memory only via ``useAuthStore``
 * (no persist middleware).
 */
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import {
  type LoginResponse,
  isEmailCodePending,
  isMfaPendingResponse,
} from "@/lib/loginResponse";
import { useAuthStore } from "@/stores/useAuthStore";

type LoginInput = { tenantSlug: string; email: string; password: string };

export function useLogin() {
  return useMutation<LoginResponse, ApiError, LoginInput>({
    mutationFn: async ({ tenantSlug, email, password }) => {
      // fix/auth-slug-in-url: slug rides in the URL; body is {email, password}.
      // Slug-less POST /auth/login on the app host returns 404.
      const resp = await apiClient.post<LoginResponse>(
        `/api/t/${encodeURIComponent(tenantSlug)}/auth/login`,
        { email, password },
      );
      return resp.data;
    },
    onMutate: () => {
      useAuthStore.getState().mfaPendingClear();
    },
    onSuccess: (data) => {
      const store = useAuthStore.getState();
      if (isMfaPendingResponse(data)) {
        // WA-2: a passkey-holding tenant user is challenged with
        // ``available_methods`` including "webauthn" (the auto-email is
        // suppressed → ``code_email_sent`` is false). Route the verify screen
        // to the passkey flow with an "Email me a code instead" fallback.
        // Otherwise the classic email-code path (discriminated by
        // ``code_email_sent`` presence) — carry the method + send-flag so the
        // verify screen renders the right variant.
        const emailCode = isEmailCodePending(data);
        const methods = data.available_methods;
        const hasWebauthn = Array.isArray(methods) && methods.includes("webauthn");
        store.mfaPendingStart(
          data.mfa_pending_token,
          data.expires_in,
          data.next_step,
          hasWebauthn
            ? {
                method: "webauthn",
                // Still present so the FE can offer the email fallback.
                codeEmailSent: data.code_email_sent ?? false,
                availableMethods: methods,
              }
            : emailCode
              ? {
                  method: "email_code",
                  codeEmailSent: data.code_email_sent ?? false,
                  availableMethods: methods,
                }
              : { method: "totp", availableMethods: methods },
        );
      } else {
        store.loginSuccess(data.access_token);
      }
    },
    onError: () => {
      useAuthStore.getState().mfaPendingClear();
    },
  });
}
